import { uploadFile } from '@lms/api-client/upload';
import { cn } from '@lms/ui/utils';
import { FileArchiveIcon, UploadCloudIcon } from 'lucide-react';
import { type DragEvent, useState } from 'react';

function formatFileSize(bytes: number): string {
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit++;
  }
  return `${value.toFixed(value >= 10 || unit === 0 ? 0 : 1)}${units[unit]}`;
}

// Drag-and-drop upload with a live progress bar, ported from the simple-lms
// admin dropzone. Handles the two-phase S3 upload via uploadFile().
export function DropZone({
  currentImage,
  folder,
  tag,
  accept = 'image/*',
  variant = 'image',
  onUploaded,
  onUploadingChange,
  className,
}: {
  /** Existing image URL to preview before a new file is picked. */
  currentImage?: string | null;
  folder: string;
  tag: string;
  accept?: string;
  /** 'image' previews a thumbnail; 'file' shows an archive icon. */
  variant?: 'image' | 'file';
  onUploaded: (url: string, filename: string) => void;
  onUploadingChange?: (uploading: boolean) => void;
  className?: string;
}) {
  const [preview, setPreview] = useState<string>();
  const [fileInfo, setFileInfo] = useState<{ name: string; size: number }>();
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string>();
  const [dragHover, setDragHover] = useState(false);

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    setError(undefined);
    setProgress(0);
    if (file.type.startsWith('image/')) setPreview(URL.createObjectURL(file));
    setFileInfo({ name: file.name, size: file.size });
    setUploading(true);
    onUploadingChange?.(true);
    try {
      const url = await uploadFile(file, folder, tag, setProgress);
      onUploaded(url, file.name);
    } catch (e) {
      setError(String(e));
    } finally {
      setUploading(false);
      onUploadingChange?.(false);
    }
  };

  const onDrop = (e: DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    setDragHover(false);
    handleFile(e.dataTransfer.files?.[0]);
  };

  const showImage = variant === 'image' && (preview || currentImage);
  const hasContent = showImage || fileInfo;

  return (
    <label
      onDragOver={(e) => {
        e.preventDefault();
        setDragHover(true);
      }}
      onDragLeave={() => setDragHover(false)}
      onDrop={onDrop}
      className={cn(
        'relative flex h-32 w-full cursor-pointer items-center gap-4 overflow-hidden rounded-md border-2 border-dashed px-4 transition',
        dragHover
          ? 'border-foreground bg-muted/50'
          : 'hover:border-foreground/40',
        className,
      )}
    >
      {hasContent ? (
        <div className="flex w-full flex-row items-center gap-4">
          {showImage ? (
            <img
              src={preview || currentImage || ''}
              alt=""
              className="h-20 w-20 rounded object-contain"
            />
          ) : (
            <div className="text-muted-foreground flex h-20 w-20 items-center justify-center">
              <FileArchiveIcon className="size-10" />
            </div>
          )}
          <div className="flex min-w-0 flex-col gap-1">
            <div className="line-clamp-1 text-sm font-medium">
              {fileInfo?.name ?? currentImage?.split('/').pop()}
            </div>
            {fileInfo?.size != null && (
              <div className="text-muted-foreground text-xs">
                {formatFileSize(fileInfo.size)}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="flex w-full items-center justify-center gap-2">
          <UploadCloudIcon className="text-muted-foreground size-6" />
          <span className="text-muted-foreground text-sm font-medium">
            Drop a file or <span className="underline">browse</span>
          </span>
        </div>
      )}
      {error && (
        <span className="text-destructive absolute bottom-1 left-4 text-xs">
          {error}
        </span>
      )}
      {uploading && (
        <div className="bg-muted absolute right-1 bottom-1 left-1 h-2 rounded-full">
          <div
            className="bg-primary h-2 rounded-full transition-[width]"
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
      <input
        type="file"
        accept={accept}
        className="absolute inset-0 cursor-pointer opacity-0"
        onChange={(e) => handleFile(e.target.files?.[0])}
      />
    </label>
  );
}
